import { element, renderSuccess } from './dom';
import { emitWidgetEvent } from './events';
import { formFields } from './fields';
import { submitContactCapture } from './submission';
import type { FormField, PublicConfig, WidgetSource } from './types';
import { validateFormField, validateFormFields } from './validations';

type FormOptions = {
  body: HTMLElement;
  config: PublicConfig;
  onDecline: () => void;
  source: WidgetSource;
};

const fieldRow = (field: FormField, errorId: string) => {
  const wrapper = element('label', 'bhi-field');
  const label = element('span', 'bhi-label', `${field.label}${field.required ? ' *' : ''}`);
  const input = element('input', 'bhi-input');
  input.name = field.name;
  input.type = field.inputType;
  input.placeholder = field.placeholder;
  input.required = field.required;
  input.setAttribute('autocomplete', field.autocomplete);
  if (field.fieldId === 'mobilePhone') input.inputMode = 'tel';

  const fieldError = element('p', 'bhi-field-error');
  fieldError.id = errorId;
  fieldError.hidden = true;
  input.setAttribute('aria-describedby', fieldError.id);

  wrapper.append(label, input, fieldError);
  return { wrapper, input, fieldError };
};

/** Builds the form, wires validation and submission, and returns it for the card to mount. */
export const createContactCaptureForm = ({ body, config, onDecline, source }: FormOptions) => {
  const { content } = config.settings;
  const fields = formFields(config.settings);
  const form = element('form', 'bhi-form');
  // Validation is owned by ./validations so the messages match the platform rules.
  form.noValidate = true;

  const inputs = new Map<string, HTMLInputElement>();
  const fieldErrors = new Map<string, HTMLParagraphElement>();

  const showFieldError = (name: string, message: string | null) => {
    const input = inputs.get(name);
    const fieldError = fieldErrors.get(name);
    if (!input || !fieldError) return;
    input.classList.toggle('bhi-input-invalid', Boolean(message));
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    fieldError.textContent = message || '';
    fieldError.hidden = !message;
  };

  fields.forEach((field, index) => {
    const row = fieldRow(field, `bhi-error-${config.id}-${index}`);
    inputs.set(field.name, row.input);
    fieldErrors.set(field.name, row.fieldError);

    // Errors appear on submit, then follow the field while it is corrected.
    row.input.addEventListener('input', () => {
      if (row.fieldError.hidden) return;
      showFieldError(field.name, validateFormField(field, row.input.value));
    });
    row.input.addEventListener('blur', () => {
      if (row.fieldError.hidden && !row.input.value.trim()) return;
      showFieldError(field.name, validateFormField(field, row.input.value));
    });

    form.appendChild(row.wrapper);
  });

  const formError = element('p', 'bhi-form-error');
  formError.hidden = true;
  formError.setAttribute('role', 'alert');
  form.appendChild(formError);

  const submitButton = element('button', 'bhi-submit', content.buttonText || 'Submit');
  submitButton.type = 'submit';
  form.appendChild(submitButton);

  if (source.mode === 'popup' && content.declineText) {
    const decline = element('button', 'bhi-decline', content.declineText);
    decline.type = 'button';
    decline.addEventListener('click', onDecline);
    form.appendChild(decline);
  }

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (submitButton.disabled) return;

    const values: Record<string, string> = {};
    fields.forEach(field => {
      values[field.name] = inputs.get(field.name)?.value.trim() || '';
    });

    const errors = validateFormFields(fields, values);
    fields.forEach(field => showFieldError(field.name, errors[field.name] || null));
    const firstInvalid = fields.find(field => errors[field.name]);
    if (firstInvalid) {
      formError.textContent = 'Please fix the highlighted fields.';
      formError.hidden = false;
      inputs.get(firstInvalid.name)?.focus();
      emitWidgetEvent('bhi:capture:error', config.id, {
        stage: 'validation',
        message: errors[firstInvalid.name],
      });
      return;
    }

    submitButton.disabled = true;
    submitButton.textContent = 'Submitting…';
    formError.hidden = true;
    emitWidgetEvent('bhi:capture:submitted', config.id, { fieldNames: Object.keys(values) });
    try {
      await submitContactCapture(source, values);
      renderSuccess(body, content.successMessage);
      emitWidgetEvent('bhi:capture:success', config.id);
    } catch (error) {
      submitButton.disabled = false;
      submitButton.textContent = content.buttonText || 'Submit';
      formError.textContent = error instanceof Error
        ? error.message
        : 'We could not submit the form. Please try again.';
      formError.hidden = false;
      emitWidgetEvent('bhi:capture:error', config.id, {
        stage: 'submission',
        message: error instanceof Error ? error.message : 'Unknown submission error',
      });
    }
  });

  return form;
};
